/************************************** THIRD-PARTY MODULES ***************************************/
import {observable, action} from 'mobx';
import {get} from 'mad-utils/lib/react';

/**************************************** PROJECT MODULES *****************************************/
import {RootStore} from '../root-store';
import {configClient} from '../../../../config/app/config-client';

import {TabIDs} from '@canimmunize/core-imports';

import {GenericConceptInstance, DoseInstance, AnyConceptInstance} from '@canimmunize/javelinscript';

/**************************************** TYPE DEFINITIONS ****************************************/
/**
 * Which vaccination type the add new vaccination type comes from
 */
export type NewVaccType = 'childAdult' | 'travel' | 'other' | 'all';

/**
 * Which screen is currently open (0 = 1st; 1 = 2nd)
 */
type ScreenNum = 0 | 1;

/********************************************* EXPORT *********************************************/
/**
 * @export AddNewVaccinationStore
 *
 * Store data regarding add new vaccination modal
 */
export class AddNewVaccinationStore {
    /***** MEMOIZED / IMMUTABLE DATA *****/
    /**
     * Vaccination options data for choosing new vaccination to add
     */
    private vaccinationOpts = {
        childAdult: null as GenericConceptInstance[],
        travel: null as GenericConceptInstance[],
        other: null as GenericConceptInstance[],
        all: null as GenericConceptInstance[],
    };

    /***** PROPS *****/
    @observable
    public currentScreen: ScreenNum = 0;
    @observable
    public pageType: TabIDs = null;
    @observable
    public isOpen = false;
    @observable
    public dose: DoseInstance = null;
    @observable
    public productConcept: AnyConceptInstance = null;

    /***** GETTERS (non-observable) *****/
    /**
     * Gets all new travel or other vaccination options
     * Memoizes result to avoid repeated re-calculation (However, these values
     * should be immutable, so it shouldn't matter)
     *
     * @param {childAdult|travel|other|all} type What vaccination options type to get
     */
    public getNewVaccinationOpts = (type: NewVaccType): GenericConceptInstance[] => {
        if (type === 'childAdult' && this.vaccinationOpts.childAdult) {
            return this.vaccinationOpts.childAdult;
        }
        if (type === 'travel' && this.vaccinationOpts.travel) return this.vaccinationOpts.travel;
        if (type === 'other' && this.vaccinationOpts.other) return this.vaccinationOpts.other;
        if (type === 'all' && this.vaccinationOpts.all) return this.vaccinationOpts.all;

        this.vaccinationOpts[type] = this.rootStore.javelinDataStore.genericConcepts.reduce(
            (acc, concept) => {
                switch (type) {
                    case 'travel':
                    case 'other':
                    case 'childAdult':
                    case 'all':
                        return acc.concat(concept);
                }
            },
            []
        );

        return this.vaccinationOpts[type];
    };

    /***** SETTERS / ACTIONS *****/
    /**
     * Sets currentScreen value (0: screen 1, 2: screen 2)
     * @param {number} newCurScreen New value of currentScreen prop
     */
    @action
    public setCurrentScreen = (newCurScreen: ScreenNum) => (this.currentScreen = newCurScreen);

    /**
     * Sets isOpen value, which controls whether add-new-vaccination modal is open
     * @param {boolean} newIsOpen New value of isOpen property; if true, modal is open
     */
    @action
    public setIsOpen = (isOpen: boolean) => {
        this.pageType = this.rootStore.activeRecordModel.recordVisitBoxesUIModel.activeVisitTypeTab;
        this.isOpen = isOpen;
        this.rootStore.uiStore.setIsOuterScrollActive(!isOpen); // Block outer scrolling
    };

    /**
     * Set active dose
     */
    @action
    public setDose = (dose: DoseInstance) => (this.dose = dose);

    /**
     * Set current tradename concept (or generic concept if no tradename concept)
     */
    @action
    public setProductConcept = (prodConcept: AnyConceptInstance) =>
        (this.productConcept = prodConcept);

    /**
     * Erase all temporary data stored in the Add New Vaccination wizard & close modal
     */
    @action
    public clear = () => {
        this.setIsOpen(false);
        setTimeout(
            action(() => {
                // this.pageType = null;
                this.dose = null;
                this.setCurrentScreen(0);
            }),
            configClient.animations.modalHideLengthMs
        );
    };

    /***** CONSTRUCTOR *****/
    /**
     * @constructor Factory method
     */
    public static new = (rootStore: RootStore): AddNewVaccinationStore => {
        const anvStore = new AddNewVaccinationStore(rootStore);
        return anvStore;
    };
    private constructor(public rootStore: RootStore) {}
}
